import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head } from "lodash";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Chip,
  Divider,
  Box,
  Typography,
} from "@material-ui/core";
import { AttachFile, Colorize, DeleteOutline } from "@material-ui/icons";
import { QueueOptions } from "../QueueOptions";
import SchedulesForm from "../SchedulesForm";
import ConfirmationModal from "../ConfirmationModal";
// ✅ FASE 2: Importar modal de preview de cambios
import ConfigChangePreviewModal from "../ConfigChangePreviewModal";
// ✅ NUEVO: Importar modal de advertencia de conexión desconectada
import ConnectionWarningModal from "../ConnectionWarningModal";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  
  // ✅ CORREGIDO: DialogContent dinámico según tema
  dialogContent: {
    padding: theme.spacing(3),
    minWidth: "600px",
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper,
  },
  
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  
  // ✅ NUEVO: Grid para campos cortos en la misma fila
  formGridShort: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  
  formGridFull: {
    gridColumn: "1 / -1",
  },
  
  // ✅ CORREGIDO: TextField dinámico según tema
  textField: {
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },
  
  // ✅ CORREGIDO: TextFieldFull dinámico según tema
  textFieldFull: {
    width: "100%",
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  
  // ✅ CORREGIDO: FormControl dinámico según tema
  formControl: {
    marginBottom: theme.spacing(1),
    minWidth: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
      color: theme.palette.text.primary,
      "& fieldset": {
        borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary || theme.palette.text.primary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      "&:hover": {
        backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
      },
    },
  },
  
  colorAdorment: {
    width: 20,
    height: 20,
    borderRadius: "4px",
    border: `1px solid ${theme.palette.divider}`,
  },
  
  // ✅ CORREGIDO: DialogActions dinámico según tema
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.grey[50],
    borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
  },
  
  // ✅ CORREGIDO: CancelButton dinámico según tema
  cancelButton: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : "#f5f5f5",
    color: theme.palette.text.primary,
    fontWeight: "600",
    padding: "10px 20px",
    borderRadius: "8px",
    textTransform: "none",
    marginRight: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : "#e0e0e0",
    },
  },
  
  saveButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "600",
    padding: "10px 20px",
    borderRadius: "8px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  
  // ✅ NUEVO: Estilos para información de profundidad
  depthInfo: {
    backgroundColor: theme.palette.info.light,
    padding: theme.spacing(2),
    borderRadius: "8px",
    marginBottom: theme.spacing(2),
  },
  
  depthTitle: {
    fontWeight: "600",
    marginBottom: theme.spacing(1),
    color: theme.palette.info.dark,
  },
  
  depthChip: {
    margin: theme.spacing(0.5),
    fontSize: "0.75rem",
  },
  
  // ✅ NUEVO: Estilos para selector FlowBuilder
  flowbuilderSelect: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f3f4f6',
    borderRadius: '8px',
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e5e7eb'}`,
  },
  flowbuilderHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  flowbuilderTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(1),
  },
  flowbuilderBody: {
    marginTop: theme.spacing(1),
  },
  flowbuilderText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  flowbuilderActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
}));

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
  greetingMessage: Yup.string(),
});

const QueueModal = ({ open, onClose, queueId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const socketManager = useContext(SocketContext);
  const history = useHistory();
  
  // ✅ Referencia para guardar opciones temporales
  const queueOptionsRef = useRef();
  
  // ✅ NUEVO: Estado para información de FlowBuilder
  const [flowbuilderInfo, setFlowbuilderInfo] = useState(null);
  const [loadingFlowbuilder, setLoadingFlowbuilder] = useState(false);

  const initialState = useMemo(() => ({
    name: "",
    color: "",
    greetingMessage: "",
    outOfHoursMessage: "",
    orderQueue: "",
    integrationId: "",
    promptId: "",
    flowBuilderId: ""
  }), []);

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queueData, setQueueData] = useState(initialState);
  
  const [tab, setTab] = useState(0);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const greetingRef = useRef();
  const [integrations, setIntegrations] = useState([]);
  const queueEditable = true;
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [schedules, setSchedules] = useState([
    { weekday: "Lunes", weekdayEn: "monday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Martes", weekdayEn: "tuesday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Miércoles", weekdayEn: "wednesday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Jueves", weekdayEn: "thursday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Viernes", weekdayEn: "friday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Sábado", weekdayEn: "saturday", startTime: "08:00", endTime: "12:00", },
    { weekday: "Domingo", weekdayEn: "sunday", startTime: "00:00", endTime: "00:00", },
  ]);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [prompts, setPrompts] = useState([]);
  // ✅ Estado para rastrear el número de opciones de chatbot
  const [chatbotOptionsCount, setChatbotOptionsCount] = useState(0);
  // ✅ NUEVO: Estado para controlar si las palabras clave están activadas
  const [keywordsEnabled, setKeywordsEnabled] = useState(false);
  // ✅ NUEVO: Estado para grupos de keywords (datos reales desde API)
  const [keywordGroups, setKeywordGroups] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showGroupForm, setShowGroupForm] = useState(false);

  // ✅ FASE 2: Estados para el modal de preview de cambios
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pendingQueueData, setPendingQueueData] = useState(null);
  
  // ✅ NUEVO: Estados para FlowBuilder selector
  const [flowBuilders, setFlowBuilders] = useState([]);
  const [selectedFlowBuilder, setSelectedFlowBuilder] = useState('');
  const [loadingFlowBuilders, setLoadingFlowBuilders] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    api.get(`/settings`).then(({ data }) => {
      if (Array.isArray(data)) {
        const scheduleType = data.find((d) => d.key === "scheduleType");
        if (scheduleType) {
          setSchedulesEnabled(scheduleType.value === "queue");
        }
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queueIntegration");

        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadQueueData = async () => {
      if (!open) {
        return;
      }

      if (!queueId) {
        // Solo para crear nuevo departamento
        setQueueData(initialState);
        setSelectedPrompt('');
        setSelectedFlowBuilder('');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        
        if (!isMounted) return;

        if (!data) {
          throw new Error('No data received from API');
        }

        const formattedData = {
          name: data.name || "",
          color: data.color || "",
          greetingMessage: data.greetingMessage || "",
          outOfHoursMessage: data.outOfHoursMessage || "",
          // ✅ CORRECCIÓN v1.3.0: Normalizar orderQueue para evitar valores null
          orderQueue: data.orderQueue !== null && data.orderQueue !== undefined ? data.orderQueue : "",
          // ✅ CORRECCIÓN v1.3.0: Normalizar integrationId para evitar valores null
          integrationId: data.integrationId !== null && data.integrationId !== undefined ? data.integrationId : "",
          promptId: data.promptId || "",
          flowBuilderId: data.flowBuilderId || ""
        };

        if (isMounted) {
          setQueueData(formattedData);
          setSelectedPrompt(data.promptId || '');
          // ✅ CORRECCIÓN: Guardar flowBuilderId solo si existe y es válido
          // No establecer el valor hasta que los flowBuilders se hayan cargado para evitar el warning de Material-UI
          // La validación y establecimiento se hará en un useEffect separado después de que se carguen los flowBuilders
          setSelectedFlowBuilder(''); // Inicializar vacío, se establecerá después cuando se carguen los flowBuilders
          if (data.schedules) {
            setSchedules(data.schedules);
          }
          setDataLoaded(true);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
          setQueueData(initialState);
          setSelectedPrompt('');
          setSelectedFlowBuilder('');
          setIsLoading(false);
        }
      }
    };

    loadQueueData();

    return () => {
      isMounted = false;
    };
  }, [open, queueId, initialState]);

  // ✅ CORRECCIÓN: Estado para TODOS los departamentos (no solo los con IA)
  const [aiQueues, setAiQueues] = useState([]);
  
  // ✅ CORRECCIÓN (30/12/2025): Estado para controlar visibilidad de pestaña de keywords
  const [showKeywordsTab, setShowKeywordsTab] = useState(false);

  // ✅ CORRECCIÓN: Función para cargar TODOS los departamentos disponibles
  const loadAIQueues = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      setAiQueues(data || []);
    } catch (err) {
      console.error("Error cargando departamentos:", err);
      setAiQueues([]);
    }
  }, []);

  // ✅ CORRECCIÓN: Cargar TODOS los departamentos cuando se abre el modal
  useEffect(() => {
    if (open) {
      loadAIQueues();
    }
  }, [open, loadAIQueues]);
  
  // ✅ NUEVO: Cargar lista de flujos FlowBuilder cuando se abre el modal
  useEffect(() => {
    if (!open) return;
    
    const loadFlowBuilders = async () => {
      try {
        setLoadingFlowBuilders(true);
        const { data } = await api.get("/flowbuilders", {
          params: { active: true }
        });
        
        setFlowBuilders(data?.records || []);
      } catch (err) {
        console.error("Error cargando flujos FlowBuilder:", err);
        setFlowBuilders([]);
      } finally {
        setLoadingFlowBuilders(false);
      }
    };
    
    loadFlowBuilders();
  }, [open]);

  // ✅ CORRECCIÓN: Establecer flowBuilderId después de cargar flowBuilders y datos del queue
  // ✅ IMPORTANTE: Esto evita el warning de Material-UI sobre valores fuera de rango
  useEffect(() => {
    if (!open || !dataLoaded || flowBuilders.length === 0) return;
    
    // ✅ Obtener flowBuilderId desde queueData (que se carga desde la API)
    const flowBuilderIdFromData = queueData.flowBuilderId;
    
    if (flowBuilderIdFromData) {
      const flowBuilderIdStr = String(flowBuilderIdFromData);
      const flowBuilderExists = flowBuilders.some(fb => String(fb.id) === flowBuilderIdStr);
      
      if (flowBuilderExists) {
        // ✅ Solo establecer si existe en la lista disponible y es diferente al actual
        if (selectedFlowBuilder !== flowBuilderIdStr) {
          setSelectedFlowBuilder(flowBuilderIdStr);
        }
      } else {
        // Si el flowBuilderId no existe en la lista, resetearlo y mostrar warning
        console.warn(`[QueueModal] FlowBuilder ID ${flowBuilderIdStr} no encontrado en la lista disponible`);
        if (selectedFlowBuilder) {
          setSelectedFlowBuilder('');
        }
      }
    } else {
      // ✅ Si no hay flowBuilderId en los datos, asegurar que esté vacío
      if (selectedFlowBuilder) {
        setSelectedFlowBuilder('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dataLoaded, flowBuilders, queueData]);
  
  // ✅ CORRECCIÓN (30/12/2025): Controlar visibilidad de pestaña de keywords
  useEffect(() => {
    const shouldShow = selectedPrompt && selectedPrompt !== null && selectedPrompt !== "";
    setShowKeywordsTab(shouldShow);
    
    // ✅ Si se quita el prompt y estamos en la pestaña de keywords, volver a la primera
    if (!shouldShow && tab > 0) {
      const keywordsTabIndex = schedulesEnabled ? 2 : 1;
      if (tab === keywordsTabIndex) {
        setTab(0);
      }
    }
  }, [selectedPrompt, tab, schedulesEnabled]);

  // ✅ CORRECCIÓN: Listener de socket para actualizar TODOS los departamentos en tiempo real
  useEffect(() => {
    if (!socketManager || !open) return;

    const companyId = localStorage.getItem("companyId");
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);
    if (!socket) return;

    // ✅ Escuchar eventos de actualización de departamentos (TODOS, no solo con IA)
    const handleQueueUpdate = (data) => {
      if (data.action === "update" && data.queue) {
        const updatedQueue = data.queue;
        
        // ✅ Actualizar o agregar departamento (sin importar si tiene IA o no)
          setAiQueues((prevQueues) => {
            const existingIndex = prevQueues.findIndex(q => q.id === updatedQueue.id);
            
            if (existingIndex >= 0) {
              // Actualizar departamento existente
              const newQueues = [...prevQueues];
              newQueues[existingIndex] = {
                id: updatedQueue.id,
                name: updatedQueue.name,
                promptId: updatedQueue.promptId
              };
              return newQueues;
            } else {
            // Agregar nuevo departamento
              return [
                ...prevQueues,
                {
                  id: updatedQueue.id,
                  name: updatedQueue.name,
                  promptId: updatedQueue.promptId
                }
              ];
            }
          });
      } else if (data.action === "create" && data.queue) {
        // ✅ Si se crea un nuevo departamento, agregarlo (sin importar si tiene IA o no)
        setAiQueues((prevQueues) => {
          const exists = prevQueues.some(q => q.id === data.queue.id);
          if (!exists) {
            return [
              ...prevQueues,
              {
                id: data.queue.id,
                name: data.queue.name,
                promptId: data.queue.promptId
              }
            ];
          }
          return prevQueues;
        });
      } else if (data.action === "delete" && data.queueId) {
        // ✅ Si se elimina un departamento, quitarlo
        setAiQueues((prevQueues) => {
          return prevQueues.filter(q => q.id !== data.queueId);
        });
      }
    };

    socket.on(`company-${companyId}-queue`, handleQueueUpdate);

    // ✅ Cleanup: remover listener cuando el componente se desmonte o el modal se cierre
    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-queue`, handleQueueUpdate);
      }
    };
  }, [socketManager, open]);

  // ✅ NUEVO: Cargar grupos de keywords cuando se abre el modal y hay queueId con IA
  useEffect(() => {
    const loadKeywordGroups = async () => {
      if (!open || !queueId || !selectedPrompt) {
        setKeywordGroups([]);
        setKeywordsEnabled(false);
        return;
      }

      setLoadingKeywords(true);
      try {
        const { data } = await api.get(`/queue/${queueId}/keywords`);
        setKeywordGroups(data || []);
        // ✅ Activar keywords si hay grupos configurados
        if (data && data.length > 0) {
          setKeywordsEnabled(true);
        }
      } catch (err) {
        // ✅ Si no hay grupos, no es error (puede estar vacío)
        if (err.response?.status !== 404) {
          toastError(err);
        }
        setKeywordGroups([]);
      } finally {
        setLoadingKeywords(false);
      }
    };

    loadKeywordGroups();
  }, [open, queueId, selectedPrompt]);

  // ✅ NUEVO: Funciones para gestionar grupos de keywords
  const handleCreateKeywordGroup = async (groupData) => {
    // ✅ VALIDAR QUE queueId SEA VÁLIDO ANTES DE HACER LA PETICIÓN
    if (!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0) {
      toast.error("Error: No se ha seleccionado un departamento válido");
      return;
    }

    try {
      const { data } = await api.post(`/queue/${queueId}/keywords`, groupData);
      setKeywordGroups([...keywordGroups, data]);
      setShowGroupForm(false);
      setEditingGroup(null);
      toast.success("Grupo de keywords creado correctamente");
    } catch (err) {
      toastError(err);
    }
  };

  const handleUpdateKeywordGroup = async (groupId, groupData) => {
    try {
      const { data } = await api.put(`/queue/${queueId}/keywords/${groupId}`, groupData);
      setKeywordGroups(keywordGroups.map(g => g.id === groupId ? data : g));
      setShowGroupForm(false);
      setEditingGroup(null);
      toast.success("Grupo de keywords actualizado correctamente");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteKeywordGroup = async (groupId) => {
    if (!window.confirm("¿Estás seguro de eliminar este grupo de keywords?")) {
      return;
    }
    try {
      await api.delete(`/queue/${queueId}/keywords/${groupId}`);
      setKeywordGroups(keywordGroups.filter(g => g.id !== groupId));
      toast.success("Grupo de keywords eliminado correctamente");
    } catch (err) {
      toastError(err);
    }
  };

  // ✅ Evitar que se limpien los datos una vez cargados
  useEffect(() => {
    if (dataLoaded && queueId && queueData.name === "") {
      // Recargar datos si se detecta limpieza inesperada
      const reloadData = async () => {
        try {
          const { data } = await api.get(`/queue/${queueId}`);
          if (data) {
            const formattedData = {
              name: data.name || "",
              color: data.color || "",
              greetingMessage: data.greetingMessage || "",
              outOfHoursMessage: data.outOfHoursMessage || "",
              // ✅ CORRECCIÓN v1.3.0: Normalizar orderQueue para evitar valores null
              orderQueue: data.orderQueue !== null && data.orderQueue !== undefined ? data.orderQueue : "",
              // ✅ CORRECCIÓN v1.3.0: Normalizar integrationId para evitar valores null
              integrationId: data.integrationId !== null && data.integrationId !== undefined ? data.integrationId : "",
              promptId: data.promptId || ""
            };
            setQueueData(formattedData);
            setSelectedPrompt(data.promptId || '');
          }
        } catch (err) {
          console.error("Error al recargar datos:", err);
        }
      };
      reloadData();
    }
  }, [dataLoaded, queueId, queueData.name]);

  // ✅ Resetear contador de opciones cuando se cierra el modal
  const handleClose = useCallback(() => {
    setChatbotOptionsCount(0);
    setQueueData(initialState);
    setSelectedPrompt('');
    setSelectedFlowBuilder('');
    setIsLoading(false);
    setDataLoaded(false);
    onClose();
  }, [initialState, onClose]);

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };


  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (queueData.mediaPath) {
      await api.delete(`/queue/${queueData.id}/media-upload`);
      setQueueData((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("queueModal.toasts.deleted"));
    }
  };

  // ✅ GUARDAR DEPARTAMENTO Y TODAS LAS OPCIONES
  const handleSaveQueue = async (values) => {
    try {
      // ✅ CORRECCIÓN FASE 2 (30/12/2025): Validación mutuamente exclusiva para CHATBOT | PROMPT | INTEGRACIÓN | FLOWBUILDER
      const hasPrompt = selectedPrompt && selectedPrompt !== null && selectedPrompt !== "";
      const hasIntegration = values.integrationId && values.integrationId !== null && values.integrationId !== "";
      const hasFlowBuilder = selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "";
      
      // ✅ Verificar si hay opciones de chatbot configuradas
      const chatbotOptionsCount = getHasChatbotOptions() ? 
        (queueOptionsRef.current.getOptionsCount ? queueOptionsRef.current.getOptionsCount() : 0) : 0;
      const hasChatbot = chatbotOptionsCount > 0;
      
      // ✅ Contar cuántas opciones están activas
      const activeCount = [hasPrompt, hasIntegration, hasChatbot, hasFlowBuilder].filter(Boolean).length;
      
      // ✅ VALIDACIÓN: Solo UNA opción puede estar activa a la vez
      if (activeCount > 1) {
        toast.error("❌ Error: Solo puedes seleccionar UNA opción: Chatbot, Prompt, Integración o FlowBuilder. No puedes seleccionar múltiples opciones simultáneamente.");
        return;
      }
      
      let savedQueueId = queueId;

      // ✅ FASE 2: Si estamos editando, verificar si hay cambios que afecten tickets
      if (queueId) {
        try {
          const { data: preview } = await api.post(`/queue/${queueId}/preview-change`, {
            promptId: selectedPrompt ? selectedPrompt : null,
            integrationId: values.integrationId || null,
            hasOptions: keywordGroups.length > 0 // Si hay opciones de chatbot
          });

          // Si hay cambios que requieren confirmación, mostrar modal
          if (preview.requiresConfirmation) {
            setPendingQueueData({
              values,
              savedQueueId,
              attachment
            });
            setPreviewData(preview);
            setPreviewModalOpen(true);
            return; // Detener aquí, el guardado se hará después de confirmar
          }
        } catch (error) {
          console.error("❌ Error al obtener preview:", error);
          // Si falla el preview, continuar con el guardado normal
        }
      }
      
      // ✅ GUARDAR DEPARTAMENTO
      if (queueId) {
        await api.put(`/queue/${queueId}`, {
          ...values, schedules, promptId: selectedPrompt ? selectedPrompt : null,
          flowBuilderId: selectedFlowBuilder ? selectedFlowBuilder : null
        });
      } else {
        const { data } = await api.post("/queue", {
          ...values, schedules, promptId: selectedPrompt ? selectedPrompt : null,
          flowBuilderId: selectedFlowBuilder ? selectedFlowBuilder : null
        });
        savedQueueId = data.id;
      }
      
      // ✅ GUARDAR ARCHIVO ADJUNTO
      if (attachment != null) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/queue/${savedQueueId}/media-upload`, formData);
      }
      
      // ✅ GUARDAR TODAS LAS OPCIONES TEMPORALES
      if (queueOptionsRef.current && queueOptionsRef.current.saveAllOptions) {
        await queueOptionsRef.current.saveAllOptions(savedQueueId);
      }
      
      toast.success(i18n.t("queues.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  // ✅ FASE 2: Función para confirmar y aplicar cambios después del preview
  const handleConfirmConfigChange = async () => {
    try {
      if (!pendingQueueData || !queueId) {
        throw new Error("No hay datos pendientes para guardar");
      }

      const { values, savedQueueId, attachment } = pendingQueueData;

      // Aplicar cambios a tickets afectados
      await api.post(`/queue/${queueId}/apply-change`, {
        changeType: previewData.changeType,
        newConfig: {
          promptId: selectedPrompt ? selectedPrompt : null,
          integrationId: values.integrationId || null
        }
      });

      // Guardar configuración del departamento
      await api.put(`/queue/${queueId}`, {
        ...values, schedules, promptId: selectedPrompt ? selectedPrompt : null,
        flowBuilderId: selectedFlowBuilder ? selectedFlowBuilder : null
      });

      // ✅ GUARDAR ARCHIVO ADJUNTO
      if (attachment != null) {
        const formData = new FormData();
        formData.append("file", attachment);
        await api.post(`/queue/${savedQueueId}/media-upload`, formData);
      }

      // ✅ GUARDAR TODAS LAS OPCIONES TEMPORALES
      if (queueOptionsRef.current && queueOptionsRef.current.saveAllOptions) {
        await queueOptionsRef.current.saveAllOptions(savedQueueId);
      }
      
      toast.success(i18n.t("queues.success"));
      
      // Cerrar modal de preview
      setPreviewModalOpen(false);
      setPendingQueueData(null);
      setPreviewData(null);
      
      // Cerrar modal principal
      handleClose();

      return { success: true, updatedTickets: previewData.affectedTickets, errors: 0 };
    } catch (error) {
      console.error("❌ Error al aplicar cambios:", error);
      throw error;
    }
  };

  const handleSaveSchedules = async (values) => {
    toast.success(i18n.t("queueModal.schedules.saveSuccess"));
    setSchedules(values);
    setTab(0);
  };

  const handleChangePrompt = (e, setFieldValue) => {
    const value = e.target.value;
    // Si el valor es vacío, establecer como null para deseleccionar
    setSelectedPrompt(value === "" ? '' : value);
    
    // ✅ CORRECCIÓN (30/12/2025): Si se selecciona PROMPT, limpiar INTEGRACIÓN, CHATBOT y FLOWBUILDER
    if (value !== "" && value !== null) {
      setQueueData(prev => ({ ...prev, integrationId: "" }));
      setSelectedFlowBuilder('');
      // ✅ Limpiar campo de integración en Formik
      if (setFieldValue && typeof setFieldValue === 'function') {
        setFieldValue('integrationId', '');
      }
      // ✅ Limpiar opciones de chatbot
      if (queueOptionsRef.current && queueOptionsRef.current.clearAllOptions) {
        queueOptionsRef.current.clearAllOptions();
      }
    }
    
    // ✅ CORRECCIÓN (30/12/2025): Resetear tab si se quita el prompt (pestaña desaparece)
    if (value === "" || value === null) {
      if (tab > 0 && (!schedulesEnabled || tab > 1)) {
        setTab(0);
      }
    }
  };
  
  // ✅ CORRECCIÓN (30/12/2025): Handler para cambio de integración
  const handleChangeIntegration = (value, setFieldValue) => {
    // ✅ Actualizar campo de integración en Formik PRIMERO
    if (setFieldValue && typeof setFieldValue === 'function') {
      setFieldValue('integrationId', value);
    }
    
    // ✅ Actualizar también en queueData para sincronizar
    setQueueData(prev => ({ ...prev, integrationId: value }));
    
    // Si se selecciona INTEGRACIÓN, limpiar PROMPT, CHATBOT y FLOWBUILDER
    if (value !== "" && value !== null) {
      // ✅ Limpiar PROMPT y FLOWBUILDER en estado
      setSelectedPrompt('');
      setSelectedFlowBuilder('');
      
      // ✅ Limpiar opciones de chatbot
      if (queueOptionsRef.current && queueOptionsRef.current.clearAllOptions) {
        queueOptionsRef.current.clearAllOptions();
      }
    }
  };
  
  // ✅ CORRECCIÓN (30/12/2025): Mostrar TODOS los departamentos (no solo los con IA)
  // Las keywords pueden transferir a CUALQUIER departamento:
  // - Si el departamento destino tiene automatización → Se asignará automáticamente
  // - Si NO tiene automatización → El ticket será atendido por un humano
  // La función transferQueue() ya maneja correctamente todos estos casos
  const aiQueuesWithPrompt = aiQueues; // Mostrar TODOS los departamentos
  
  // ✅ CORRECCIÓN (30/12/2025): Función para calcular si hay opciones de chatbot (se llama dentro del render)
  const getHasChatbotOptions = () => {
    return queueOptionsRef.current && 
      queueOptionsRef.current.getOptionsCount && 
      queueOptionsRef.current.getOptionsCount() > 0;
  };

  // ✅ Valores iniciales estables para Formik
  // Excluir color de las dependencias para evitar reinicialización cuando solo cambia el color
  // El color se maneja directamente en Formik sin afectar otros campos
  const formikInitialValues = useMemo(() => {
    const values = {
      name: queueData.name || "",
      color: queueData.color || "",
      greetingMessage: queueData.greetingMessage || "",
      outOfHoursMessage: queueData.outOfHoursMessage || "",
      // ✅ CORRECCIÓN v1.3.0: Normalizar orderQueue para evitar valores null
      orderQueue: queueData.orderQueue !== null && queueData.orderQueue !== undefined ? queueData.orderQueue : "",
      // ✅ CORRECCIÓN v1.3.0: Normalizar integrationId para evitar valores null
      integrationId: queueData.integrationId !== null && queueData.integrationId !== undefined ? queueData.integrationId : "",
      promptId: queueData.promptId || ""
    };
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueData.name, queueData.greetingMessage, queueData.outOfHoursMessage, queueData.orderQueue, queueData.integrationId, queueData.promptId, dataLoaded]);

  // ✅ COMPONENTE INTERNO: Formulario para crear/editar grupo de keywords
  const KeywordGroupForm = ({ group, aiQueues, queueId, onSubmit, onCancel }) => {
    const { user } = useContext(AuthContext);
    const { companyId } = user || {};
    
    const [groupName, setGroupName] = useState(group?.groupName || '');
    const [transferQueueId, setTransferQueueId] = useState(group?.transferQueueId || '');
    const [keywords, setKeywords] = useState(group?.keywords?.map(k => k.keyword || k).join(', ') || '');
    const [whatsappId, setWhatsappId] = useState(group?.whatsappId || '');
    const [whatsapps, setWhatsapps] = useState([]);
    const [loadingWhatsapps, setLoadingWhatsapps] = useState(false);
    const [priority, setPriority] = useState(group?.priority || 0);
    const [enabled, setEnabled] = useState(group?.enabled !== undefined ? group.enabled : true);
    const [autoTransfer, setAutoTransfer] = useState(group?.autoTransfer !== undefined ? group.autoTransfer : true);
    const [requireConfirmation, setRequireConfirmation] = useState(group?.requireConfirmation || false);
    const [matchType, setMatchType] = useState(group?.matchType || 'contains');
    const [caseSensitive, setCaseSensitive] = useState(group?.caseSensitive || false);
    const [errors, setErrors] = useState({}); // ✅ NUEVO: Estado para errores de validación
    
    // ✅ NUEVO: Estados para modal de advertencia de conexión desconectada
    const [connectionWarningOpen, setConnectionWarningOpen] = useState(false);
    const [connectionWarningData, setConnectionWarningData] = useState(null);

    // ✅ Cargar conexiones WhatsApp disponibles
    useEffect(() => {
      const loadWhatsapps = async () => {
        if (!companyId) return;
        
        setLoadingWhatsapps(true);
        try {
          const { data } = await api.get(`/whatsapp`, { params: { companyId, session: 0 } });
          setWhatsapps(data || []);
        } catch (err) {
          console.error("Error cargando conexiones WhatsApp:", err);
          toastError(err);
        } finally {
          setLoadingWhatsapps(false);
        }
      };

      loadWhatsapps();
    }, [companyId]);

    const handleSubmit = (e) => {
      e.preventDefault();
      
      // ✅ Limpiar errores previos
      setErrors({});
      
      // ✅ VALIDACIÓN: Nombre del grupo es obligatorio
      if (!groupName || !groupName.trim()) {
        setErrors({ groupName: 'El nombre del grupo es requerido' });
        toast.error('El nombre del grupo es requerido');
        return;
      }
      
      // ✅ VALIDACIÓN: Departamento destino es obligatorio
      if (!transferQueueId || transferQueueId === '') {
        setErrors({ transferQueueId: 'Debes seleccionar un departamento destino' });
        toast.error('Debes seleccionar un departamento destino');
        return;
      }
      
      // ✅ VALIDACIÓN: Palabras clave son obligatorias
      const keywordsArray = keywords.split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);
      
      if (keywordsArray.length === 0) {
        setErrors({ keywords: 'Debes agregar al menos una palabra clave' });
        toast.error('Debes agregar al menos una palabra clave');
        return;
      }

      // ✅ VALIDACIÓN: Verificar estado de conexión WhatsApp antes de guardar (solo si hay whatsappId)
      if (whatsappId && whatsappId !== "" && whatsappId !== 0) {
        const selectedWhatsappObj = whatsapps.find(w => w.id === Number(whatsappId));
        if (selectedWhatsappObj && selectedWhatsappObj.status !== 'CONNECTED') {
          const whatsappName = selectedWhatsappObj.name || `Conexión ${selectedWhatsappObj.id}`;
          
          // ✅ MOSTRAR MODAL DE ADVERTENCIA en lugar de toast rápido
          setConnectionWarningData({
            connectionName: whatsappName,
            connectionStatus: selectedWhatsappObj.status
          });
          setConnectionWarningOpen(true);
          return;
        }
      }

      // ✅ Si todas las validaciones pasan, enviar datos
      onSubmit({
        groupName: groupName.trim(),
        transferQueueId: Number(transferQueueId),
        keywords: keywordsArray,
        whatsappId: whatsappId && whatsappId !== '' ? Number(whatsappId) : null,
        enabled,
        autoTransfer,
        requireConfirmation,
        priority: Number(priority) || 0,
        matchType,
        caseSensitive
      });
    };

    return (
      <>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <TextField
            label="Nombre del Grupo"
            value={groupName}
            onChange={(e) => {
              setGroupName(e.target.value);
              // ✅ Limpiar error cuando el usuario escribe
              if (errors.groupName) {
                setErrors(prev => ({ ...prev, groupName: undefined }));
              }
            }}
            fullWidth
            variant="outlined"
            margin="dense"
            required
            error={!!errors.groupName}
            helperText={errors.groupName}
            style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <FormControl 
            fullWidth 
            variant="outlined" 
            margin="dense" 
            required 
            error={!!errors.transferQueueId}
          >
            <InputLabel>Departamento Destino</InputLabel>
            <Select
              value={transferQueueId}
              onChange={(e) => {
                setTransferQueueId(e.target.value);
                // ✅ Limpiar error cuando el usuario selecciona un departamento
                if (errors.transferQueueId) {
                  setErrors(prev => ({ ...prev, transferQueueId: undefined }));
                }
              }}
              label="Departamento Destino"
              required
              style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
            >
              <MenuItem value="">Seleccionar...</MenuItem>
              {aiQueuesWithPrompt.map((queue) => (
                <MenuItem key={queue.id} value={queue.id}>
                  {queue.name}
                </MenuItem>
              ))}
            </Select>
            {errors.transferQueueId && (
              <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
                {errors.transferQueueId}
              </div>
            )}
          </FormControl>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <TextField
            label="Palabras Clave (separadas por comas)"
            value={keywords}
            onChange={(e) => {
              setKeywords(e.target.value);
              // ✅ Limpiar error cuando el usuario escribe
              if (errors.keywords) {
                setErrors(prev => ({ ...prev, keywords: undefined }));
              }
            }}
            fullWidth
            variant="outlined"
            margin="dense"
            placeholder="comprar, precio, producto, venta"
            helperText={errors.keywords || "Separa las palabras clave con comas"}
            required
            error={!!errors.keywords}
            style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
          />
        </div>

        <div style={{ marginBottom: '15px' }}>
          <FormControl 
            fullWidth 
            variant="outlined" 
            margin="dense" 
            error={!!errors.whatsappId}
          >
            <InputLabel>
              <span role="img" aria-label="Conexión WhatsApp">📱</span> Conexión WhatsApp (Opcional)
            </InputLabel>
            <Select
              value={whatsappId}
              onChange={(e) => {
                setWhatsappId(e.target.value);
                // ✅ Limpiar error cuando el usuario selecciona una conexión
                if (errors.whatsappId) {
                  setErrors(prev => ({ ...prev, whatsappId: undefined }));
                }
              }}
              label={
                <span>
                  <span role="img" aria-label="Conexión WhatsApp">📱</span> Conexión WhatsApp (Opcional)
                </span>
              }
              disabled={loadingWhatsapps}
              style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
              renderValue={() => {
                if (whatsappId === "" || !whatsappId) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                      <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                      <span>Usar conexión del ticket (por defecto)</span>
                    </div>
                  );
                }
                const whatsapp = whatsapps.find(w => w.id === Number(whatsappId));
                if (!whatsapp) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                      <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                      <span>Seleccione una conexión</span>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {whatsapp.name || `Conexión ${whatsapp.id}`}
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                         whatsapp.name ? whatsapp.name : 
                         `Conexión ${whatsapp.id}`}
                      </span>
                    </div>
                  </div>
                );
              }}
            >
              <MenuItem value="">
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="Por defecto">🔄</span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                    <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                      Usar conexión del ticket (por defecto)
                    </span>
                    <span style={{ fontSize: '12px', color: '#666' }}>
                      El sistema usará la conexión WhatsApp del ticket original
                    </span>
                  </div>
                </div>
              </MenuItem>
              {whatsapps?.length > 0 &&
                whatsapps.map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="WhatsApp">📱</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                            {whatsapp.name || `Conexión ${whatsapp.id}`}
                          </span>
                          <span 
                            style={{ 
                              fontSize: '10px', 
                              padding: '2px 6px', 
                              borderRadius: '10px',
                              backgroundColor: whatsapp.status === 'CONNECTED' ? '#25d366' : '#e1306c',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          >
                            {whatsapp.status}
                          </span>
                        </div>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                           whatsapp.name ? whatsapp.name : 
                           `Conexión ${whatsapp.id}`}
                        </span>
                      </div>
                    </div>
                  </MenuItem>
                ))}
            </Select>
            {errors.whatsappId && (
              <div style={{ color: '#f44336', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>
                {errors.whatsappId}
              </div>
            )}
          </FormControl>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <TextField
            label="Prioridad"
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            variant="outlined"
            margin="dense"
            helperText="Mayor número = mayor prioridad"
            style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
          />
          
          <FormControl variant="outlined" margin="dense">
            <InputLabel>Tipo de Coincidencia</InputLabel>
            <Select
              value={matchType}
              onChange={(e) => setMatchType(e.target.value)}
              label="Tipo de Coincidencia"
              style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#fff' }}
            >
              <MenuItem value="contains">Contiene</MenuItem>
              <MenuItem value="exact">Exacta</MenuItem>
              <MenuItem value="partial">Parcial</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <span>Grupo activado</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
            <input
              type="checkbox"
              checked={autoTransfer}
              onChange={(e) => setAutoTransfer(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <span>Transferir inmediatamente al detectar</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
            <input
              type="checkbox"
              checked={requireConfirmation}
              onChange={(e) => setRequireConfirmation(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <span>Requerir confirmación del usuario</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', color: theme.mode === 'dark' ? theme.palette.text.primary : '#333' }}>
            <input
              type="checkbox"
              checked={caseSensitive}
              onChange={(e) => setCaseSensitive(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <span>Sensible a mayúsculas/minúsculas</span>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <Button
            variant="outlined"
            onClick={onCancel}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
          >
            {group ? 'Actualizar' : 'Crear'} Grupo
          </Button>
        </div>
      </form>

      {/* ✅ NUEVO: Modal de advertencia de conexión desconectada */}
      <ConnectionWarningModal
        open={connectionWarningOpen}
        onClose={() => {
          setConnectionWarningOpen(false);
          setConnectionWarningData(null);
        }}
        connectionName={connectionWarningData?.connectionName || ""}
        connectionStatus={connectionWarningData?.connectionStatus || ""}
      />
    </>
    );
  };

  return (
    <div className={classes.root}>
    <ConfirmationModal
        title={i18n.t("queueModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      ></ConfirmationModal>
    <Dialog
    maxWidth="md"
    fullWidth={true}
    open={open}
    onClose={handleClose}
    scroll="paper"
  >
    <DialogTitle>
      {queueId
        ? `${i18n.t("queueModal.title.edit")}`
        : `${i18n.t("queueModal.title.add")}`}
       <div style={{ display: "none" }}>
        <input
          type="file"
          ref={attachmentFile}
          onChange={(e) => handleAttachmentFile(e)}
        />
      </div>
    </DialogTitle>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_, v) => {
            setTab(v);
          }}
          aria-label="disabled tabs example"
        >
          <Tab label={i18n.t("queueModal.tabs.queueData")} />
          {schedulesEnabled && <Tab label={i18n.t("queueModal.tabs.serviceHours")} />}
          {/* ✅ NUEVA PESTAÑA: Solo visible si el departamento tiene IA configurada */}
          {showKeywordsTab && <Tab label="Palabras Clave" />}
        </Tabs>
        {tab === 0 && (
          <Paper style={{ backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
            {isLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </div>
            ) : (
            <Formik
              enableReinitialize={dataLoaded}
              initialValues={formikInitialValues}
              validationSchema={QueueSchema}
              validateOnMount={false}
              validateOnChange={false}
              validateOnBlur={true}
              onSubmit={async (values, actions) => {
                try {
                  await handleSaveQueue(values);
                } catch (err) {
                  toastError(err);
                } finally {
                  actions.setSubmitting(false);
                }
              }}
            >
              {({ touched, errors, isSubmitting, values, setFieldValue }) => {
                // ✅ Calcular hasIntegration, hasPrompt, hasFlowBuilder y hasChatbot dentro del render de Formik usando values
                // ✅ CORRECCIÓN: Asegurar que siempre sean booleanos (no strings)
                const hasPromptInForm = !!(selectedPrompt && selectedPrompt !== null && selectedPrompt !== "");
                const hasIntegrationInForm = !!(values.integrationId && values.integrationId !== null && values.integrationId !== "");
                const hasFlowBuilderInForm = !!(selectedFlowBuilder && selectedFlowBuilder !== null && selectedFlowBuilder !== "");
                const hasChatbotInForm = chatbotOptionsCount > 0;
                
                return (
                <Form>
                  <DialogContent className={classes.dialogContent}>
                    {/* ✅ DISEÑO DE 2 COLUMNAS */}
                    <div className={classes.formGrid}>
                      {/* COLUMNA 1 */}
                      <div>
                        <TextField
                          label="Nombre del Departamento"
                          autoFocus
                          name="name"
                          value={values.name || ""}
                          onChange={(e) => setFieldValue('name', e.target.value)}
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          className={classes.textFieldFull}
                        />
                      </div>

                      {/* COLUMNA 2 */}
                      <div>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          className={classes.formControl}
                        >
                          <InputLabel id="integrationId-selection-label">
                            Integración
                          </InputLabel>
                          <Field
                            as={Select}
                            label="Integración"
                            name="integrationId"
                            id="integrationId"
                            placeholder="Integración"
                            labelId="integrationId-selection-label"
                            value={values.integrationId !== null && values.integrationId !== undefined ? values.integrationId : ""}
                            disabled={!!(hasPromptInForm || hasChatbotInForm || hasFlowBuilderInForm)}
                            onChange={(e) => handleChangeIntegration(e.target.value, setFieldValue)}
                          >
                            <MenuItem value="">Sin integración</MenuItem>
                            {integrations.map((integration) => (
                              <MenuItem key={integration.id} value={integration.id}>
                                {integration.name}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </div>
                    </div>

                    {/* ✅ CAMPOS CORTOS EN LA MISMA FILA */}
                    <div className={classes.formGridShort}>
                      <TextField
                        label="Color"
                        name="color"
                        id="color"
                        value={values.color || ""}
                        onChange={(e) => setFieldValue('color', e.target.value)}
                        onFocus={() => {
                          setColorPickerModalOpen(true);
                          greetingRef.current.focus();
                        }}
                        error={touched.color && Boolean(errors.color)}
                        helperText={touched.color && errors.color}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <div
                                style={{ backgroundColor: values.color }}
                                className={classes.colorAdorment}
                              ></div>
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => setColorPickerModalOpen(true)}
                            >
                              <Colorize />
                            </IconButton>
                          ),
                        }}
                        variant="outlined"
                        margin="dense"
                        className={classes.textFieldFull}
                      />
                      
                      <TextField
                        label="Orden de la Cola"
                        name="orderQueue"
                        type="number"
                        value={values.orderQueue !== null && values.orderQueue !== undefined ? values.orderQueue : ""}
                        onChange={(e) => setFieldValue('orderQueue', e.target.value)}
                        error={touched.orderQueue && Boolean(errors.orderQueue)}
                        helperText={touched.orderQueue && errors.orderQueue}
                        variant="outlined"
                        margin="dense"
                        className={classes.textFieldFull}
                      />
                    </div>
                    
                    <ColorPicker
                      open={colorPickerModalOpen}
                      handleClose={() => setColorPickerModalOpen(false)}
                      onChange={(color) => {
                        // ✅ CORREGIDO: Solo actualizar el campo color en Formik sin afectar otros campos
                        setFieldValue('color', color);
                        // ✅ NO actualizar queueData aquí para evitar que formikInitialValues se recalcule
                        // El estado local se actualizará cuando se guarde el formulario
                      }}
                    />

                    {/* ✅ PROMPT DE IA + FLOWBUILDER EN LA MISMA FILA */}
                    <div className={classes.formGrid}>
                      <div>
                        <FormControl
                          margin="dense"
                          variant="outlined"
                          className={classes.formControl}
                        >
                          <InputLabel>Prompt de IA</InputLabel>
                          <Select
                            labelId="dialog-select-prompt-label"
                            id="dialog-select-prompt"
                            name="promptId"
                            value={selectedPrompt || ""}
                            onChange={(e) => handleChangePrompt(e, setFieldValue)}
                            label="Prompt de IA"
                            disabled={!!(hasIntegrationInForm || hasChatbotInForm || hasFlowBuilderInForm)}
                            MenuProps={{
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              getContentAnchorEl: null,
                            }}
                          >
                            <MenuItem value="">Sin prompt</MenuItem>
                            {prompts.map((prompt) => (
                              <MenuItem key={prompt.id} value={prompt.id}>
                                {prompt.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                      
                      <div>
                        <FormControl
                          variant="outlined"
                          margin="dense"
                          className={classes.formControl}
                        >
                          <InputLabel id="flowBuilderId-selection-label">
                            FlowBuilder
                          </InputLabel>
                          <Select
                            label="FlowBuilder"
                            name="flowBuilderId"
                            id="flowBuilderId"
                            labelId="flowBuilderId-selection-label"
                            value={selectedFlowBuilder || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedFlowBuilder(value);
                              // ✅ Si se selecciona FlowBuilder, limpiar otros campos
                              if (value && value !== "") {
                                setSelectedPrompt('');
                                setFieldValue('integrationId', '');
                                // Limpiar opciones de chatbot
                                if (queueOptionsRef.current && queueOptionsRef.current.clearOptions) {
                                  queueOptionsRef.current.clearOptions();
                                }
                                setChatbotOptionsCount(0);
                              }
                            }}
                            disabled={!!(loadingFlowBuilders || hasPromptInForm || hasIntegrationInForm || hasChatbotInForm)}
                          >
                            <MenuItem value="">Sin flujo</MenuItem>
                            {flowBuilders.map((flow) => (
                              <MenuItem key={flow.id} value={flow.id}>
                                {flow.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </div>
                    {/* ✅ CAMPOS DE MENSAJES - ANCHO COMPLETO */}
                    <div className={classes.formGridFull}>
                      <TextField
                        label="Mensaje de Saludo"
                        multiline
                        inputRef={greetingRef}
                        rows={4}
                        fullWidth
                        name="greetingMessage"
                        value={values.greetingMessage || ""}
                        onChange={(e) => setFieldValue('greetingMessage', e.target.value)}
                        error={
                          touched.greetingMessage &&
                          Boolean(errors.greetingMessage)
                        }
                        helperText={
                          touched.greetingMessage && errors.greetingMessage
                        }
                        variant="outlined"
                        margin="dense"
                        className={classes.textFieldFull}
                      />
                      {schedulesEnabled && (
                        <TextField
                          label="Mensaje Fuera de Horario"
                          multiline
                          inputRef={greetingRef}
                          rows={4}
                          fullWidth
                          name="outOfHoursMessage"
                          value={values.outOfHoursMessage || ""}
                          onChange={(e) => setFieldValue('outOfHoursMessage', e.target.value)}
                          error={
                            touched.outOfHoursMessage &&
                            Boolean(errors.outOfHoursMessage)
                          }
                          helperText={
                            touched.outOfHoursMessage && errors.outOfHoursMessage
                          }
                          variant="outlined"
                          margin="dense"
                          className={classes.textFieldFull}
                        />
                      )}
                    </div>
                    {/* ✅ CORRECCIÓN (30/12/2025): Mostrar mensaje si chatbot está deshabilitado */}
                    {(hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) && (
                      <div style={{ 
                        padding: '10px', 
                        marginBottom: '10px',
                        backgroundColor: theme.mode === 'dark' ? '#374151' : '#fff3cd',
                        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ffc107'}`,
                        borderRadius: '4px',
                        color: theme.mode === 'dark' ? theme.palette.text.secondary : '#856404'
                      }}>
                        <span role="img" aria-label="warning">⚠️</span> Las opciones de chatbot están deshabilitadas porque {hasFlowBuilderInForm ? 'tienes un FlowBuilder seleccionado' : hasPromptInForm ? 'tienes un Prompt de IA seleccionado' : 'tienes una Integración seleccionada'}. Solo puedes usar una automatización a la vez.
                      </div>
                    )}
                    <div style={{ opacity: (hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) ? 0.5 : 1, pointerEvents: (hasPromptInForm || hasIntegrationInForm || hasFlowBuilderInForm) ? 'none' : 'auto' }}>
                      <QueueOptions 
                        queueId={queueId}
                        ref={queueOptionsRef}
                        onOptionAdded={() => {
                          // ✅ Si se agrega una opción de chatbot, limpiar PROMPT e INTEGRACIÓN
                          setSelectedPrompt('');
                          setQueueData(prev => ({ ...prev, integrationId: "" }));
                          
                          // ✅ Limpiar campo de integración en Formik
                          if (setFieldValue && typeof setFieldValue === 'function') {
                            setFieldValue('integrationId', '');
                          }
                          
                          // ✅ Forzar actualización del contador (se actualizará automáticamente con onOptionsChanged)
                          setTimeout(() => {
                            if (queueOptionsRef.current && queueOptionsRef.current.getOptionsCount) {
                              const count = queueOptionsRef.current.getOptionsCount();
                              setChatbotOptionsCount(count);
                            }
                          }, 0);
                        }}
                        onOptionsChanged={(optionsCount) => {
                          // ✅ Actualizar estado local para que el render se actualice
                          setChatbotOptionsCount(optionsCount);
                        }}
                      />
                    </div>
                    
                    {(queueData.mediaPath || attachment) && (
                    <Grid xs={12} item>
                      <Button startIcon={<AttachFile />}>
                        {attachment != null
                          ? attachment.name
                          : queueData.mediaName}
                      </Button>
                      {queueEditable && (
                        <IconButton
                          onClick={() => setConfirmationOpen(true)}
                          color="secondary"
                        >
                          <DeleteOutline />
                        </IconButton>
                      )}
                    </Grid>
                  )}
                  </DialogContent>
                  <DialogActions className={classes.dialogActions}>
                    {/* ✅ BOTÓN ADJUNTAR OCULTO - No se usa */}
                    {/* {!attachment && !queueData.mediaPath && queueEditable && (
                      <Button
                        color="primary"
                        onClick={() => attachmentFile.current.click()}
                        disabled={isSubmitting}
                        variant="outlined"
                      >
                        {i18n.t("queueModal.buttons.attach")}
                      </Button>
                    )} */}
                    
                    <div style={{ flex: 1 }} />
                    
                    <Button
                      className={classes.cancelButton}
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                    
                    <div className={classes.btnWrapper}>
                      <Button
                        className={classes.saveButton}
                        type="submit"
                        disabled={isSubmitting}
                        variant="contained"
                      >
                        {queueId ? "Actualizar" : "Agregar"}
                        {isSubmitting && (
                          <CircularProgress
                            size={24}
                            className={classes.buttonProgress}
                          />
                        )}
                      </Button>
                    </div>
                  </DialogActions>
                </Form>
                );
              }}
            </Formik>
            )}
          </Paper>
        )}
        {/* ✅ PESTAÑA HORARIOS: Solo si schedulesEnabled es true */}
        {tab === 1 && schedulesEnabled && (
          <Paper style={{ padding: 20, backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
            <SchedulesForm
              loading={false}
              onSubmit={handleSaveSchedules}
              initialValues={schedules}
              labelSaveButton={i18n.t("queueModal.schedules.saveButton")}
            />
          </Paper>
        )}
        {/* ✅ PESTAÑA PALABRAS CLAVE: Índice dinámico según si hay pestaña de horarios */}
        {((schedulesEnabled && tab === 2) || (!schedulesEnabled && tab === 1)) && showKeywordsTab && (
          <Paper style={{ padding: 20, backgroundColor: theme.mode === 'dark' ? '#1f2937' : theme.palette.background.paper }}>
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ 
                display: 'flex', 
                alignItems: 'center', 
                margin: '0 0 10px 0',
                color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
              }}>
                <span role="img" aria-label="robot">🤖</span> Configuración de Palabras Clave para IA
              </h3>
              <p style={{ 
                color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666', 
                margin: '0 0 20px 0' 
              }}>
                Este departamento tiene IA configurada. Puedes definir palabras clave que activarán 
                la transferencia automática de tickets a este departamento.
              </p>
              
              {/* ✅ NUEVO: Switch para activar/desactivar palabras clave */}
              <div style={{ 
                border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                borderRadius: '8px', 
                padding: '15px',
                backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <h4 style={{ 
                      margin: '0 0 5px 0', 
                      fontSize: '16px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      <span role="img" aria-label="target">🎯</span> Activar Detección por Palabras Clave
                    </h4>
                    <p style={{ 
                      margin: '0', 
                      fontSize: '14px', 
                      color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666' 
                    }}>
                      Habilita la transferencia automática basada en palabras clave
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}>
                      <input 
                        type="checkbox" 
                        checked={keywordsEnabled}
                        onChange={(e) => setKeywordsEnabled(e.target.checked)}
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          marginRight: '8px',
                          cursor: 'pointer'
                        }} 
                      />
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '500',
                        color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                      }}>
                        {keywordsEnabled ? 'Activado' : 'Desactivado'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* ✅ CONDICIONAL: Solo mostrar si las palabras clave están activadas */}
            {keywordsEnabled && (
              <div style={{ marginBottom: 20 }}>
                <h4 style={{ 
                  color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                  marginBottom: '15px'
                }}>
                  <span role="img" aria-label="memo">📝</span> Grupos de Palabras Clave
                </h4>
                
                {/* ✅ LOADING STATE */}
                {loadingKeywords && (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress size={24} />
                    <p style={{ marginTop: '10px', color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666' }}>
                      Cargando grupos de keywords...
                    </p>
                  </div>
                )}

                {/* ✅ LISTA DE GRUPOS DESDE API */}
                {!loadingKeywords && keywordGroups.length === 0 && (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '20px',
                    border: `1px dashed ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`,
                    borderRadius: '8px',
                    color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666'
                  }}>
                    <p>No hay grupos de keywords configurados aún.</p>
                    <p style={{ fontSize: '12px', marginTop: '5px' }}>
                      Haz clic en "Agregar Nuevo Grupo" para crear uno.
                    </p>
                  </div>
                )}

                {/* ✅ RENDERIZAR GRUPOS DESDE API */}
                {!loadingKeywords && keywordGroups.map((group, index) => {
                  const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b'];
                  const color = colors[index % colors.length];
                  const bgColor = color === '#1976d2' ? '#e3f2fd' : 
                                 color === '#388e3c' ? '#e8f5e8' : 
                                 color === '#f57c00' ? '#fff3e0' :
                                 color === '#7b1fa2' ? '#f3e5f5' : '#fce4ec';
                  
                  const transferQueue = aiQueues.find(q => q.id === group.transferQueueId);
                  
                  return (
                    <div key={group.id} style={{ 
                      border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                      borderRadius: '8px', 
                      padding: '15px',
                      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9',
                      marginBottom: '15px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <h5 style={{ 
                          margin: '0', 
                          fontSize: '16px', 
                          color: color 
                        }}>
                          <span role="img" aria-label="label">🏷️</span> {group.groupName}
                          {!group.enabled && (
                            <span style={{ 
                              marginLeft: '10px', 
                              fontSize: '12px', 
                              color: '#999',
                              fontStyle: 'italic'
                            }}>
                              (Desactivado)
                            </span>
                          )}
                        </h5>
                        <div style={{ marginLeft: 'auto' }}>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            style={{ marginRight: '5px' }}
                            onClick={() => {
                              setEditingGroup(group);
                              setShowGroupForm(true);
                            }}
                          >
                            Editar
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="secondary"
                            onClick={() => handleDeleteKeywordGroup(group.id)}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </div>
                      
                      {/* ✅ KEYWORDS DEL GRUPO */}
                      <div style={{ marginBottom: '10px' }}>
                        {group.keywords && group.keywords.length > 0 ? (
                          group.keywords.map((kw, kwIndex) => (
                            <span key={kwIndex} style={{ 
                              display: 'inline-block',
                              backgroundColor: bgColor,
                              color: color,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              margin: '2px',
                              fontSize: '14px'
                            }}>
                              {kw.keyword || kw}
                            </span>
                          ))
                        ) : (
                          <span style={{ 
                            color: theme.mode === 'dark' ? theme.palette.text.secondary : '#999',
                            fontSize: '12px',
                            fontStyle: 'italic'
                          }}>
                            Sin keywords configuradas
                          </span>
                        )}
                      </div>
                      
                      {/* ✅ DEPARTAMENTO DESTINO */}
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <span style={{ 
                          fontSize: '14px', 
                          fontWeight: '500',
                          color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                        }}>
                          Transferir a:
                        </span>
                        <span style={{ 
                          fontSize: '14px',
                          color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666',
                          fontWeight: '500'
                        }}>
                          {transferQueue ? transferQueue.name : `ID: ${group.transferQueueId}`}
                        </span>
                        {group.priority > 0 && (
                          <span style={{ 
                            fontSize: '12px',
                            color: theme.mode === 'dark' ? theme.palette.text.secondary : '#999',
                            backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            Prioridad: {group.priority}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* ✅ BOTÓN AGREGAR NUEVO GRUPO */}
                <div style={{ textAlign: 'center', marginTop: '15px' }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    style={{ 
                      borderStyle: 'dashed',
                      borderColor: '#1976d2',
                      color: '#1976d2'
                    }}
                    disabled={!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0}
                    onClick={() => {
                      if (!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0) {
                        toast.error("Debes guardar el departamento primero antes de crear grupos de keywords");
                        return;
                      }
                      setEditingGroup(null);
                      setShowGroupForm(true);
                    }}
                  >
                    + Agregar Nuevo Grupo
                  </Button>
                  {(!queueId || isNaN(Number(queueId)) || Number(queueId) <= 0) && (
                    <p style={{ 
                      fontSize: '12px', 
                      color: theme.mode === 'dark' ? theme.palette.text.secondary : '#666',
                      marginTop: '8px'
                    }}>
                      Guarda el departamento primero para crear grupos de keywords
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ✅ FORMULARIO PARA CREAR/EDITAR GRUPO */}
            {showGroupForm && (
              <div style={{ 
                border: `2px solid ${theme.mode === 'dark' ? '#4B5563' : '#1976d2'}`, 
                borderRadius: '8px', 
                padding: '20px',
                backgroundColor: theme.mode === 'dark' ? '#374151' : '#f0f7ff',
                marginBottom: '20px'
              }}>
                <h4 style={{ 
                  color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                  marginBottom: '15px'
                }}>
                  {editingGroup ? <><span role="img" aria-label="edit">✏️</span> Editar Grupo</> : <><span role="img" aria-label="add">➕</span> Nuevo Grupo de Keywords</>}
                </h4>
                
                <KeywordGroupForm
                  group={editingGroup}
                  aiQueues={aiQueuesWithPrompt}
                  queueId={queueId}
                  onSubmit={async (formData) => {
                    if (editingGroup) {
                      await handleUpdateKeywordGroup(editingGroup.id, formData);
                    } else {
                      await handleCreateKeywordGroup(formData);
                    }
                  }}
                  onCancel={() => {
                    setShowGroupForm(false);
                    setEditingGroup(null);
                  }}
                />
              </div>
            )}

            {/* ✅ CONDICIONAL: Solo mostrar si las palabras clave están activadas */}
            {keywordsEnabled && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ 
                    color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                    marginBottom: '15px'
                  }}>
                    <span role="img" aria-label="chart">📊</span> Estadísticas
                  </h4>
                  <div style={{ 
                    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                    borderRadius: '8px', 
                    padding: '15px',
                    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9'
                  }}>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      • Transferencias automáticas: <strong>15</strong>
                    </p>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      • Última activación: <strong>hace 2 horas</strong>
                    </p>
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <h4 style={{ 
                    color: theme.mode === 'dark' ? theme.palette.text.primary : '#333',
                    marginBottom: '15px'
                  }}>
                    <span role="img" aria-label="magnifying glass">🔍</span> Vista Previa
                  </h4>
                  <div style={{ 
                    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#ddd'}`, 
                    borderRadius: '8px', 
                    padding: '15px',
                    backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9f9f9'
                  }}>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      Usuario escribe: <em>"Quiero comprar un producto"</em>
                    </p>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      ↓
                    </p>
                    <p style={{ 
                      margin: '5px 0', 
                      fontSize: '14px',
                      color: theme.mode === 'dark' ? theme.palette.text.primary : '#333'
                    }}>
                      <span role="img" aria-label="robot">🤖</span> Detectado: <strong>"comprar"</strong> → Transferir a <strong>"{queueData.name}"</strong>
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* ✅ MENSAJE CUANDO ESTÁ DESACTIVADO */}
            {!keywordsEnabled && (
              <div style={{ 
                border: `1px solid ${theme.mode === 'dark' ? '#f57c00' : '#ffcc02'}`, 
                borderRadius: '8px', 
                padding: '20px',
                backgroundColor: theme.mode === 'dark' ? '#78350f' : '#fff9c4',
                textAlign: 'center'
              }}>
                <h4 style={{ 
                  margin: '0 0 10px 0', 
                  color: theme.mode === 'dark' ? '#fbbf24' : '#f57c00' 
                }}>
                  <span role="img" aria-label="warning">⚠️</span> Palabras Clave Desactivadas
                </h4>
                <p style={{ 
                  margin: '0', 
                  fontSize: '14px', 
                  color: theme.mode === 'dark' ? theme.palette.text.secondary : theme.palette.text.primary 
                }}>
                  Para configurar palabras clave, primero activa la detección automática arriba.
                </p>
              </div>
            )}
          </Paper>
        )}
      </Dialog>

      {/* ✅ FASE 2: Modal de preview de cambios de configuración */}
      <ConfigChangePreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPendingQueueData(null);
          setPreviewData(null);
        }}
        previewData={previewData}
        onConfirm={handleConfirmConfigChange}
        loading={isLoading}
        type="queue"
      />
    </div>
  );
};

export default QueueModal;
